import { createContext, useContext, useMemo, type ReactNode } from "react";

import { MarkdownContent } from "../../../shared/ui";
import { databasePointNames } from "../service/itineraryOutputService";
import type { PublicItinerary } from "../types";

const ItineraryPointNamesContext = createContext<readonly string[]>([]);
const ItineraryPointLabelsContext = createContext<ReadonlyMap<string, string> | undefined>(undefined);

/** 为当前客户行程的 Markdown 描述注入数据库考察点名称。 */
export function ItineraryMarkdownProvider({
  itinerary,
  children,
  annotatePointCategories = false,
}: {
  itinerary: PublicItinerary;
  children: ReactNode;
  annotatePointCategories?: boolean;
}) {
  const pointNames = useMemo(() => databasePointNames(itinerary), [itinerary]);
  const pointLabels = useMemo(() => annotatePointCategories ? new Map(
    itinerary.days.flatMap((day) => [...day.nodes, ...(day.parallelTracks ?? []).flatMap((track) => track.nodes)])
      .filter((node) => node.nodeType === "point" && (node.categoryLabel === "企业" || node.categoryLabel === "非遗"))
      .map((node) => [node.title.trim(), node.categoryLabel!] as const),
  ) : undefined, [itinerary, annotatePointCategories]);
  return <ItineraryPointNamesContext.Provider value={pointNames}>
    <ItineraryPointLabelsContext.Provider value={pointLabels}>{children}</ItineraryPointLabelsContext.Provider>
  </ItineraryPointNamesContext.Provider>;
}

/** 渲染行程描述，并对当前行程涉及的数据库考察点加粗标红。 */
export function ItineraryMarkdown({ content, className }: { content: string; className?: string }) {
  const pointNames = useContext(ItineraryPointNamesContext);
  const pointLabels = useContext(ItineraryPointLabelsContext);
  return <MarkdownContent className={className} content={content} highlightTerms={pointNames} highlightLabels={pointLabels} />;
}
